import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import emojiRegex from "emoji-regex";
import { useDispatch, useSelector } from "react-redux";
import { fetchFormQuiz } from "../store/reducers/quizzesSlice";
import { getOrganizationBCustomerId } from "../store/reducers/organizationSlice";

const getFormQuizQuestionText = (question, language) => {
  if (language === "danish") {
    return question?.question || question?.question_dk || "-";
  }
  return question?.question || question?.question_en || "-";
};

const getFormQuizOptionText = (option, language) => {
  if (language === "danish") {
    return option?.text || option?.text_dk || option?.text_en || "-";
  }
  return option?.text || option?.text_en || option?.text_dk || "-";
};

const normalizeFormQuizPayload = (payload) => {
  const raw = Array.isArray(payload) ? payload : payload?.data || [];

  if (Array.isArray(raw) && raw.length) {
    if (raw[0]?.question) {
      return [
        {
          id: "inline",
          displayName: "",
          displayName_dk: "",
          questions: raw,
        },
      ];
    }

    if (raw[0]?.questions) {
      return raw.map((quiz, index) => ({
        id: quiz?.id || `quiz-${index}`,
        displayName: quiz?.name || "",
        displayName_dk: quiz?.name_dk || "",
        questions: quiz?.questions || [],
      }));
    }

    return raw.flatMap((item) =>
      Object.entries(item || {}).map(([key, quiz]) => ({
        id: key,
        displayName: quiz?.name || "",
        displayName_dk: quiz?.name_dk || "",
        questions: quiz?.questions || [],
      })),
    );
  }

  if (raw && typeof raw === "object") {
    return Object.entries(raw).map(([key, quiz]) => ({
      id: key,
      displayName: quiz?.name || "",
      displayName_dk: quiz?.name_dk || "",
      questions: quiz?.questions || [],
    }));
  }

  return [];
};

const ActionCardsForm = () => {
  const { id } = useParams();
  const [t] = useTranslation();
  const jwtToken = localStorage.getItem("jwt_token");
  const [selectedLanguage, setSelectedLanguage] = useState("danish");
  const [successfulSubmission, setSuccessfulSubmission] = useState(false);
  const [trainingOptions, setTrainingOptions] = useState([]);
  const [actioncardname, setActionCardName] = useState("");
  const search = useLocation().search;
  const searchParams = new URLSearchParams(search);
  const customerIdFromQuery = searchParams.get("customer_id");
  const [loading, setLoading] = useState(false);
  const org = searchParams.get("org");
  const acNo = searchParams.get("action_card_nb");
  const dispatch = useDispatch();
  const { organizationDetails } = useSelector(
    (state) => state.organization,
  );

  const languageLabels = {
    danish: {
      Yes: "Ja",
      No: "Nej",
      "--Other--": "--Andet--",
      "--Select--": "--Vælg--",
      Yellow: "Gul",
      Red: "Rød",
    },
    english: {
      Yes: "Yes",
      No: "No",
      "--Other--": "--Other--",
      "--Select--": "--Select--",
      Yellow: "Yellow",
      Red: "Red",
    },
  };

  const realIncidentOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const injuredParty = [
    { value: "", label: "--Select--" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const potentialWitnesses = [
    { value: "", label: "--Select--" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const yellowRedOption = [
    { value: "yellow", label: "Yellow" },
    { value: "red", label: "Red" },
  ];

  const numberYesOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
    { value: "13", label: "13" },
    { value: "14", label: "14" },
    { value: "15", label: "15" },
    { value: "16", label: "16" },
    { value: "17", label: "17" },
  ];

  const quantityOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
    { value: "13", label: "13" },
    { value: "14", label: "14" },
    { value: "15", label: "15" },
    { value: "16", label: "16" },
    { value: "17", label: "17" },
  ];

  const numberNAOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
    { value: "13", label: "13" },
    { value: "14", label: "14" },
    { value: "15", label: "15" },
    { value: "16", label: "16" },
    { value: "17", label: "17" },
  ];

  const [formValues, setFormValues] = useState({
    selected: realIncidentOptions[0],
    selectedInjuredPartyOption: injuredParty[0],
    selectedPotentialWitnessesOptions: potentialWitnesses[0],
    selectedtrainingOption: trainingOptions ? trainingOptions[0] : "Null",
    selectednumberYesOptions: numberYesOptions[0],
    selectedquantityOptions: quantityOptions[0],
    selectednumberNAOptions: numberNAOptions[0],
    selectedFile: "",
    name: "",
    inputValue: "",
    injuredPartyName: "",
    injuredPartyPhoneNo: "",
    injuredPartyEmail: "",
    potentialWitnessesName: "",
    potentialWitnessesPhoneNo: "",
    potentialWitnessesEmail: "",
    yellowRed: "",
  });

  const ClearData = (e) => {
    setFormValues({
      selected: realIncidentOptions[0],
      selectedInjuredPartyOption: injuredParty[0],
      selectedPotentialWitnessesOptions: potentialWitnesses[0],
      selectedtrainingOption: trainingOptions ? trainingOptions[0] : "Null",
      selectednumberYesOptions: numberYesOptions[0],
      selectedquantityOptions: quantityOptions[0],
      selectednumberNAOptions: numberNAOptions[0],
      selectedFile: "",
      name: "",
      inputValue: "",
      injuredPartyName: "",
      injuredPartyPhoneNo: "",
      injuredPartyEmail: "",
      potentialWitnessesName: "",
      potentialWitnessesPhoneNo: "",
      potentialWitnessesEmail: "",
    });
  };

  const [errorMsg, setErrorMsg] = useState(false);

  const handleChange = (selectedOption) => {
    setFormValues({ ...formValues, selected: selectedOption });
    setSelectedOption("");
    setShowDiv(false);
  };

  const handleTrainingOptionChange = (trainingOption) => {
    setFormValues({ ...formValues, selectedtrainingOption: trainingOption });
  };

  const handleInjuredPartyOptionChange = (injuredOption) => {
    setFormValues({ ...formValues, selectedInjuredPartyOption: injuredOption });
  };

  const handleRealIncidentRedYellow = (injuredOption) => {
    setFormValues({ ...formValues, yellowRed: injuredOption });
  };

  const handlePotentialWitnessesChange = (potentialWitnessesOption) => {
    setFormValues({
      ...formValues,
      selectedPotentialWitnessesOptions: potentialWitnessesOption,
    });
  };

  const handleNumberYesOptionsChange = (numberOptions) => {
    setFormValues({ ...formValues, selectednumberYesOptions: numberOptions });
  };
  const handleQuantityOptionsChange = (quantityOptions) => {
    setFormValues({ ...formValues, selectedquantityOptions: quantityOptions });
  };
  const handleNumberNAOptionsChange = (numberNAOptions) => {
    setFormValues({ ...formValues, selectednumberNAOptions: numberNAOptions });
  };

  const [showDiv, setShowDiv] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [emojiError, setEmojiError] = useState("");
  const [readQuizChoice, setReadQuizChoice] = useState("");
  const [readQuiz, setReadQuiz] = useState(null);
  const [readAnswers, setReadAnswers] = useState({});
  const [readQuizError, setReadQuizError] = useState("");
  const regex = emojiRegex();
  const orgDetails = Array.isArray(organizationDetails)
    ? organizationDetails[0]
    : organizationDetails;
  const isReportingCategoryCard = ["60", "210"].includes(
    String(acNo ?? "").trim(),
  );

  const canShowReportingCategoryOptions =
    Boolean(orgDetails?.show_reporting_category) && isReportingCategoryCard;

  const handleRadioChange = (event) => {
    const value = event.target.value;
    setSelectedOption(value);
    setShowDiv(value === "training");
    if (value !== "read") {
      setReadQuizChoice("");
      setReadQuiz(null);
      setReadAnswers({});
      setReadQuizError("");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setFormValues({ ...formValues, selectedFile: file });
      setErrorMsg(false);
    } else {
      setErrorMsg(`${file.name} - File exceeds size limit`);
    }
  };

  const handleTextareaChange = (event) => {
    const value = event.target.value;
    setFormValues({ ...formValues, inputValue: value });

    if (regex.test(value)) {
      setEmojiError(
        selectedLanguage === "danish"
          ? "Emojis er ikke tilladt og vil forårsage en fejl, hvis de indsendes"
          : "Emojis are not allowed and will create an error if submitted",
      );
    } else {
      setEmojiError("");
    }
  };

  const onNameChangeHandler = (event) => {
    setFormValues({ ...formValues, name: event.target.value });
  };

  const onInjuredPartyNameChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      injuredPartyName: event.target.value,
    });
  };

  const handleReadChoice = async (value) => {
    setReadQuizChoice(value);
    if (value === "yes") {
      await handleReadQuiz();
    } else {
      setReadQuiz(null);
      setReadAnswers({});
      setReadQuizError("");
    }
  };

  const handleReadQuiz = async () => {
    if (!acNo) return;
    try {
      const res = await dispatch(fetchFormQuiz(acNo)).unwrap();
      const mapped = normalizeFormQuizPayload(res);

      if (!mapped.length) {
        setReadQuiz(null);
        setReadAnswers({});
        setReadQuizError(
          selectedLanguage === "danish"
            ? "Quizzen blev ikke genereret, fordi der ikke er spørgsmål nok til at generere en quiz."
            : "Quiz was not generated because there are not enough questions to generate a quiz.",
        );
        return;
      }

      const matched = mapped[0];

      setReadQuiz(matched || null);
      setReadAnswers({});
      setReadQuizError("");
    } catch (err) {
      setReadQuizError(
        selectedLanguage === "danish"
          ? "Quizzen blev ikke genereret, fordi der ikke er spørgsmål nok til at generere en quiz."
          : "Quiz was not generated because there are not enough questions to generate a quiz.",
      );
      setReadQuiz(null);
    }
  };

  const handleReadAnswerChange = (qId, optionId, isMulti) => {
    setReadAnswers((prev) => {
      if (!isMulti) {
        return { ...prev, [qId]: [optionId] };
      }
      const prevOptions = prev[qId] || [];
      if (prevOptions.includes(optionId)) {
        return { ...prev, [qId]: prevOptions.filter((id) => id !== optionId) };
      }
      return { ...prev, [qId]: [...prevOptions, optionId] };
    });
  };

  const onInjuredPartyPhoneNoChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      injuredPartyPhoneNo: event.target.value,
    });
  };

  const onInjuredPartyEmailChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      injuredPartyEmail: event.target.value,
    });
  };

  const onPotentialWitnessesNameChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      potentialWitnessesName: event.target.value,
    });
  };

  const onPotentialWitnessesPhoneNoChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      potentialWitnessesPhoneNo: event.target.value,
    });
  };

  const onPotentialWitnessesEmailChangeHandler = (event) => {
    setFormValues({
      ...formValues,
      potentialWitnessesEmail: event.target.value,
    });
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    ClearData();
  };

  const characterCountLabel = `${formValues.inputValue.length} of 4000 ${t(
    "actionCardForm.label.maximum characters",
  )}`;

  const characterCountLabelofName = `${formValues.name.length} of 45 ${t(
    "actionCardForm.label.maximum characters",
  )}`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    let quizAnswers = null;
    let increaseScore = 0;

    if (readQuizChoice === "yes" && readQuiz) {
      if (Object.keys(readAnswers).length !== readQuiz.questions.length) {
        toast.error(
          selectedLanguage === "danish"
            ? "Besvar venligst alle spørgsmål"
            : "Please answer all questions",
        );
        setLoading(false);
        return;
      }
      quizAnswers = Object.keys(readAnswers).map((qId) => ({
        question_id: Number(qId),
        ids: readAnswers[qId],
      }));
      increaseScore = 1;
    }

    continueFormSubmit(quizAnswers, increaseScore);
  };

  const continueFormSubmit = (quizAnswers, increaseScore = 0) => {
    const bodyFormData = new FormData();
    bodyFormData.append("org", searchParams.get("org"));
    bodyFormData.append("afd", searchParams.get("afd"));
    bodyFormData.append("sted", searchParams.get("sted"));
    bodyFormData.append("nb_set", searchParams.get("nb_set"));
    bodyFormData.append("action_card_nb", searchParams.get("action_card_nb"));
    bodyFormData.append("version", searchParams.get("version"));
    bodyFormData.append("leverance", searchParams.get("leverance"));
    bodyFormData.append("year", searchParams.get("year"));
    bodyFormData.append("month", searchParams.get("month"));
    bodyFormData.append("type", searchParams.get("type"));
    bodyFormData.append("sharp", formValues.selected.value);
    bodyFormData.append("ac_documentation1", formValues.selectedFile);
    bodyFormData.append("qr_code", selectedOption);
    bodyFormData.append(
      "has_never_miss",
      selectedOption === "near_miss" ? 1 : 0,
    );
    bodyFormData.append(
      "has_prevention",
      selectedOption === "prevention" ? 1 : 0,
    );
    bodyFormData.append(
      "has_doubt_about_reporting",
      selectedOption === "doubt_about_reporting" ? 1 : 0,
    );
    if (formValues.selectedtrainingOption?.value) {
      bodyFormData.append(
        "employee",
        formValues.selectedtrainingOption?.value == "other"
          ? formValues.name
          : formValues.selectedtrainingOption?.value,
      );
      bodyFormData.append(
        "user_id",
        formValues.selectedtrainingOption?.value === "other"
          ? 0
          : formValues.selectedtrainingOption?.user_id ?? 0,
      );
    }
    bodyFormData.append(
      "numbers_yes",
      formValues.selectednumberYesOptions.value,
    );
    bodyFormData.append("numbers_no", formValues.selectedquantityOptions.value);
    bodyFormData.append("numbers_na", formValues.selectednumberNAOptions.value);
    bodyFormData.append("comment", formValues.inputValue);
    bodyFormData.append(
      "injured_option",
      formValues.selectedInjuredPartyOption.value,
    );
    bodyFormData.append(
      "witeness_option",
      formValues.selectedPotentialWitnessesOptions.value,
    );
    bodyFormData.append("injured_option_name", formValues.injuredPartyName);
    bodyFormData.append("injured_option_phone", formValues.injuredPartyPhoneNo);
    bodyFormData.append("injured_option_email", formValues.injuredPartyEmail);
    bodyFormData.append(
      "witeness_option_name",
      formValues.potentialWitnessesName,
    );
    bodyFormData.append(
      "witeness_option_phone",
      formValues.potentialWitnessesPhoneNo,
    );
    bodyFormData.append(
      "real_incident_type",
      isYellowRedRequiredOrg(org) &&
        formValues.yellowRed &&
        formValues.yellowRed.value
        ? formValues.yellowRed.value
        : null,
    );
    bodyFormData.append(
      "real_incident_type_count",
      isYellowRedRequiredOrg(org) &&
        formValues.yellowRedNumber &&
        formValues.yellowRedNumber.value
        ? formValues.yellowRedNumber.value
        : 0,
    );

    bodyFormData.append(
      "witeness_option_email",
      formValues.potentialWitnessesEmail,
    );
    bodyFormData.append("language", selectedLanguage);

    if (quizAnswers && quizAnswers.length) {
      bodyFormData.append("quiz_answers", JSON.stringify(quizAnswers));
      bodyFormData.append("increase_score", increaseScore);
    } else {
      bodyFormData.append("increase_score", 0);
    }

    fetch(`${process.env.REACT_APP_API_URL}/send-registration-form/${id}`, {
      method: "POST",
      body: bodyFormData,
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          setSuccessfulSubmission(true);
          toast.success(data.message);
        }
        ClearData();
        setLoading(false);
        return Promise.reject(data.error);
      })
      .catch((error) => {
        toast.error(error);
        setLoading(false);
      });
  };

  const employeeData = () => {
    fetch(
      `${
        process.env.REACT_APP_API_URL
      }/form-employee-list?id=${id}&acno=${searchParams.get("action_card_nb")}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      },
    )
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          const empData = [];
          const result = data.data.data;
          result.map((one) => {
            if (one.employee_id) {
              empData.push({
                // value: `${one.first_name} ${one.last_name ?? ""}`,
                // label: `${one.first_name} ${one.last_name ?? ""}`,
                value: `${one.employee_id}`,
                label: `${one.employee_id}`,
                user_id: `${one.id}`,
              });
            }
          });
          empData.sort((a, b) => {
            const isAlphaA = /^[A-Za-z]/.test(a.value);
            const isAlphaB = /^[A-Za-z]/.test(b.value);

            if (isAlphaA && !isAlphaB) return -1;
            if (!isAlphaA && isAlphaB) return 1;

            return a.value.localeCompare(b.value);
          });
          empData.unshift({ value: "Null", label: "--Select--" });
          // Add data to the end
          empData.push({ value: "Other", label: "--Other--" });
          const actioncard_name = {
            danish: data.data.action_name_danish,
            eng: data.data.action_name_eng,
          };
          setActionCardName(actioncard_name);
          setTrainingOptions(empData);
          setFormValues({
            ...formValues,
            selectedtrainingOption: empData[0],
          });
        }
        return Promise.reject(data.error);
      })
      .catch((error) => {
        toast.error(error);
      });
  };
  useEffect(() => {
    const fetchData = async () => {
      employeeData();
      await dispatch(getOrganizationBCustomerId(id));
    };

    if (id) {
      fetchData();
    }
  }, [dispatch, id]);
  useEffect(() => {
    if (
      formValues.yellowRed?.value === "yellow" &&
      org === "DNK" &&
      ["21", "60", "76"].includes(acNo?.toString()) &&
      !formValues.yellowRedNumber
    ) {
      setFormValues((prev) => ({
        ...prev,
        yellowRedNumber: { value: 1, label: "1" },
      }));
    }
  }, [formValues.yellowRed, org, acNo]);

  // Helper function to check if yellow/red is required for org
  const isYellowRedRequiredOrg = (org) => {
    if (!org) return false;
    const orgLower = org.toLowerCase();
    return (
      (orgLower === "gentofte_life" && acNo != 1) ||
      (orgLower === "gentofte life" && acNo != 1) ||
      (orgLower === "dnk" && acNo != 1)
    );
  };

  return (
    <>
      {successfulSubmission ? (
        <div className="container d-flex justify-content-center align-items-center vh-100">
          <div className="text-center">
            <img
              src="https://action-cards.com/wp-content/uploads/2022/05/Logo_positiv_CMYK_tagline_h%E2%94%9C%E2%95%95jre_EN_udvidet.svg"
              alt="logo"
              width="80%"
              style={{ backgroundColor: "transparent" }}
            />
            <hr />
            <h3>
              <b>Your data has been submitted</b>
            </h3>
            <h6>
              “Thank you–you have just helped to make your organization a safer
              place!”
            </h6>
          </div>
        </div>
      ) : (
        <>
          <div className="container justify-content-center mt-2">
            <ul className="nav nav-pills mb-3" id="pills-tab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link btn-dark ${
                    selectedLanguage === "danish" ? "active" : ""
                  }`}
                  id="pills-profile-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-profile"
                  type="button"
                  role="tab"
                  aria-controls="pills-profile"
                  aria-selected="true"
                  onClick={() => handleLanguageChange("danish")}
                >
                  {t("IntroductionVideo.label.Danish")}
                </button>
              </li>

              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link btn-dark mx-3 ${
                    selectedLanguage === "english" ? "active" : ""
                  }`}
                  id="pills-home-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-home"
                  type="button"
                  role="tab"
                  aria-controls="pills-home"
                  aria-selected="false"
                  onClick={() => handleLanguageChange("english")}
                >
                  {t("IntroductionVideo.label.English")}
                </button>
              </li>
            </ul>
            <hr />

            <div className="box tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="pills-home-tab"
              >
                <form onSubmit={handleSubmit}>
                  <div className="container mt-3">
                    <div className="row">
                      <div className="col-12">
                        <h4>
                          <b>You have scanned this ACTION-CARD: </b>
                        </h4>
                        <h5 className="mt-3">
                          {selectedLanguage === "danish"
                            ? actioncardname?.danish
                            : actioncardname?.eng}
                        </h5>
                        <h6 className="mt-4">
                          Press SEND to complete the registration.
                        </h6>
                        <h6 className="mt-4">
                          <b>Real incident</b>
                        </h6>
                        <div className="mt-3">
                          <Select
                            className="basic-single"
                            classNamePrefix="select"
                            options={realIncidentOptions.map((option) => ({
                              value: option.value,
                              label:
                                languageLabels[selectedLanguage][option.label],
                            }))}
                            onChange={handleChange}
                            value={formValues.selected}
                            placeholder={
                              languageLabels[selectedLanguage]["--Select--"]
                            }
                            autoFocus={true}
                            styles={{
                              singleValue: (provided) => ({
                                ...provided,
                                fontSize: "18px", // Adjust the font size as needed
                              }),
                              option: (provided) => ({
                                ...provided,
                                fontSize: "18px", // Adjust the font size as needed
                              }),
                              // Add more styles as needed
                            }}
                          />
                        </div>

                        <>
                          <div className="mt-3">
                            <h6>
                              <b>Enter your employee name or ID</b>
                            </h6>
                            <div className="w-100">
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={trainingOptions.map((option) => {
                                  return {
                                    value: option.value,
                                    label: option.label,
                                    user_id: option.user_id,
                                  };
                                })}
                                onChange={handleTrainingOptionChange}
                                value={formValues.selectedtrainingOption}
                                autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>
                          </div>
                          {formValues.selectedtrainingOption?.value ===
                            "Other" && (
                            <div className="mt-3">
                              <label forHtml="name">
                                <h6 className="radiolabel">
                                  <b>
                                    Enter your Email ID
                                    <span className="danger">*</span>
                                  </b>
                                </h6>
                              </label>
                              <div>
                                <input
                                  type="email"
                                  className="form-control"
                                  id="name"
                                  maxLength="45"
                                  value={formValues.name}
                                  style={{ width: "50%", color: "black" }}
                                  onChange={(e) => {
                                    onNameChangeHandler(e);
                                  }}
                                />
                              </div>
                              <small className="mt-4">
                                {characterCountLabelofName}
                              </small>
                            </div>
                          )}
                        </>

                        {formValues.selected.value === "yes" && (
                          <div>
                            {isYellowRedRequiredOrg(org) && (
                              <div className={"mt-3"}>
                                <h6>
                                  <b>Select the Real incident type</b>
                                </h6>

                                <div
                                  className="mt-3 w-100"
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "20px",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <div className="w-100">
                                    <Select
                                      required={true}
                                      className="basic-single"
                                      classNamePrefix="select"
                                      options={yellowRedOption.map(
                                        (option) => ({
                                          value: option.value,
                                          label:
                                            languageLabels[selectedLanguage]?.[
                                              option.label
                                            ] || option.label,
                                        }),
                                      )}
                                      onChange={(selected) => {
                                        handleRealIncidentRedYellow(selected);
                                        if (
                                          selected?.value !== "yellow" ||
                                          org !== "DNK" ||
                                          !["21", "60", "76"].includes(
                                            acNo?.toString(),
                                          )
                                        ) {
                                          setFormValues((prev) => ({
                                            ...prev,
                                            yellowRedNumber: {
                                              value: 1,
                                              label: "1",
                                            },
                                          }));
                                        }
                                      }}
                                      value={
                                        yellowRedOption
                                          .map((option) => ({
                                            value: option.value,
                                            label:
                                              languageLabels[
                                                selectedLanguage
                                              ]?.[option.label] || option.label,
                                          }))
                                          .find(
                                            (opt) =>
                                              opt.value ===
                                              formValues.yellowRed?.value,
                                          ) || null
                                      }
                                      placeholder={
                                        languageLabels[selectedLanguage]?.[
                                          "--Select--"
                                        ] || "--Select--"
                                      }
                                      styles={{
                                        singleValue: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                        option: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                        placeholder: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                      }}
                                    />
                                  </div>
                                  {formValues.yellowRed?.value === "yellow" &&
                                    org === "DNK" &&
                                    ["21", "60", "76"].includes(
                                      acNo?.toString(),
                                    ) && (
                                      <div className="w-100">
                                        <h6>
                                          <b>Choose number</b>
                                        </h6>

                                        <div className="w-100">
                                          <Select
                                            required={true}
                                            classNamePrefix="select"
                                            placeholder="1"
                                            options={Array.from(
                                              { length: 30 },
                                              (_, i) => ({
                                                value: i + 1,
                                                label: `${i + 1}`,
                                              }),
                                            )}
                                            onChange={(selected) => {
                                              setFormValues((prev) => ({
                                                ...prev,
                                                yellowRedNumber: selected,
                                              }));
                                            }}
                                            value={
                                              formValues.yellowRedNumber || null
                                            }
                                            styles={{
                                              singleValue: (provided) => ({
                                                ...provided,
                                                fontSize: "18px",
                                              }),
                                              option: (provided) => ({
                                                ...provided,
                                                fontSize: "18px",
                                              }),
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <h6>
                                <b>
                                  To the injured party: would it be OK if we
                                  register your full name and contact details
                                  for documentation purposes?
                                </b>
                              </h6>
                              <span className="mt-5">
                                <h6>
                                  The purpose of storing this information is
                                  solely to enable you, upon request, to
                                  document this incident. Your personal
                                  information (i.e. your name and contact
                                  details) will be automatically deleted from
                                  the system after six months.
                                </h6>
                              </span>
                              <div className="w-full mt-3">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  options={injuredParty.map((option) => ({
                                    value: option.value,
                                    label:
                                      languageLabels[selectedLanguage][
                                        option.label
                                      ],
                                  }))}
                                  onChange={handleInjuredPartyOptionChange}
                                  value={formValues.selectedInjuredPartyOption}
                                  autoFocus={true}
                                  styles={{
                                    singleValue: (provided) => ({
                                      ...provided,
                                      fontSize: "18px", // Adjust the font size as needed
                                    }),
                                    option: (provided) => ({
                                      ...provided,
                                      fontSize: "18px", // Adjust the font size as needed
                                    }),
                                    // Add more styles as needed
                                  }}
                                />
                              </div>
                            </div>

                            {formValues.selectedInjuredPartyOption.value ===
                              "yes" && (
                              <div>
                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>
                                        First name and surname of injured party
                                      </b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyName}
                                      onChange={onInjuredPartyNameChangeHandler}
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Phone number for injured party</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyPhoneNo}
                                      onChange={
                                        onInjuredPartyPhoneNoChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Email address for injured party</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyEmail}
                                      onChange={
                                        onInjuredPartyEmailChangeHandler
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <h6>
                                <b>
                                  For potential witness: would it be OK with you
                                  if we register your full name and contact
                                  details?
                                </b>
                              </h6>
                              <span className="mt-5">
                                <h6>
                                  The purpose of storing this information is
                                  solely to document this incident. Your
                                  personal information (i.e. name and contact
                                  details) will be automatically deleted from
                                  the system after 6 months.
                                </h6>
                              </span>
                              <div className="w-full mt-3">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  options={potentialWitnesses.map((option) => ({
                                    value: option.value,
                                    label:
                                      languageLabels[selectedLanguage][
                                        option.label
                                      ],
                                  }))}
                                  onChange={handlePotentialWitnessesChange}
                                  value={
                                    formValues.selectedPotentialWitnessesOptions
                                  }
                                  autoFocus={true}
                                  styles={{
                                    singleValue: (provided) => ({
                                      ...provided,
                                      fontSize: "18px", // Adjust the font size as needed
                                    }),
                                    option: (provided) => ({
                                      ...provided,
                                      fontSize: "18px", // Adjust the font size as needed
                                    }),
                                    // Add more styles as needed
                                  }}
                                />
                              </div>
                            </div>

                            {formValues.selectedPotentialWitnessesOptions
                              .value === "yes" && (
                              <div>
                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>First name and surname of witness</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.potentialWitnessesName}
                                      onChange={
                                        onPotentialWitnessesNameChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Phone number of witness</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={
                                        formValues.potentialWitnessesPhoneNo
                                      }
                                      onChange={
                                        onPotentialWitnessesPhoneNoChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Email of witness</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      value={formValues.potentialWitnessesEmail}
                                      style={{ color: "black" }}
                                      onChange={
                                        onPotentialWitnessesEmailChangeHandler
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {formValues.selected.value === "no" && (
                          <div>
                            <div className="mt-4">
                              <h6>
                                <b>
                                  Is the QR code scanned to register for
                                  training or reading the Action-card?
                                  <span className="danger">*</span>
                                </b>{" "}
                              </h6>
                            </div>
                            <div className="mt-4">
                              <input
                                id="contactChoice1"
                                name="contact"
                                type="radio"
                                value="training"
                                required
                                checked={selectedOption === "training"}
                                onChange={handleRadioChange}
                              />
                              <label
                                for="contactChoice1"
                                className="radiolabel mx-2"
                                style={{ fontSize: "18px" }}
                              >
                                Yes, training
                              </label>
                              <br />
                              <input
                                type="radio"
                                id="contactChoice2"
                                name="contact"
                                value="read"
                                required
                                checked={selectedOption === "read"}
                                onChange={handleRadioChange}
                              />
                              <label
                                for="contactChoice2"
                                className="radiolabel mx-2"
                                style={{ fontSize: "18px" }}
                              >
                                Yes, read AC
                              </label>
                              {canShowReportingCategoryOptions && (
                                <>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoiceNearMissEn"
                                    name="contact"
                                    value="near_miss"
                                    required
                                    checked={selectedOption === "near_miss"}
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoiceNearMissEn"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Near miss
                                  </label>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoicePreventionEn"
                                    name="contact"
                                    value="prevention"
                                    required
                                    checked={selectedOption === "prevention"}
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoicePreventionEn"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Prevention
                                  </label>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoiceDoubtEn"
                                    name="contact"
                                    value="doubt_about_reporting"
                                    required
                                    checked={
                                      selectedOption === "doubt_about_reporting"
                                    }
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoiceDoubtEn"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Doubt about reporting
                                  </label>
                                </>
                              )}
                            </div>
                            {selectedOption === "read" && (
                              <div className="mt-3">
                                <p
                                  className="radiolabel"
                                  style={{ fontSize: "18px" }}
                                >
                                  You have read an Action card. Do you want to
                                  increase your score by answering three
                                  questions?
                                </p>
                                <div>
                                  <input
                                    id="readChoiceYesEn"
                                    name="readChoiceEn"
                                    type="radio"
                                    value="yes"
                                    checked={readQuizChoice === "yes"}
                                    onChange={() => handleReadChoice("yes")}
                                  />
                                  <label
                                    htmlFor="readChoiceYesEn"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Yes
                                  </label>
                                  <br />
                                  <input
                                    id="readChoiceNoEn"
                                    name="readChoiceEn"
                                    type="radio"
                                    value="no"
                                    checked={readQuizChoice === "no"}
                                    onChange={() => handleReadChoice("no")}
                                  />
                                  <label
                                    htmlFor="readChoiceNoEn"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    No
                                  </label>
                                </div>

                                {readQuizError && (
                                  <p
                                    className="text-danger"
                                    style={{ fontSize: "16px" }}
                                  >
                                    {readQuizError}
                                  </p>
                                )}

                                {readQuiz && !readQuizError && (
                                  <div className="mt-3">
                                    {readQuiz.questions?.map((q, idx) => (
                                      <div key={q.id} className="mb-3">
                                        <p className="radiolabel">
                                          <b>
                                            Q{idx + 1}:{" "}
                                            {getFormQuizQuestionText(
                                              q,
                                              selectedLanguage,
                                            )}
                                          </b>
                                        </p>
                                        <div style={{ paddingLeft: "1rem" }}>
                                          {q.correct_answers === 1
                                            ? q.options?.map((opt) => (
                                                <div key={opt.id}>
                                                  <input
                                                    type="radio"
                                                    name={`read-q-${q.id}`}
                                                    value={opt.id}
                                                    checked={
                                                      readAnswers[q.id]?.[0] ===
                                                      opt.id
                                                    }
                                                    onChange={() =>
                                                      handleReadAnswerChange(
                                                        q.id,
                                                        opt.id,
                                                        false,
                                                      )
                                                    }
                                                  />
                                                  <label className="mx-2">
                                                    {getFormQuizOptionText(
                                                      opt,
                                                      selectedLanguage,
                                                    )}
                                                  </label>
                                                </div>
                                              ))
                                            : q.options?.map((opt) => (
                                                <div key={opt.id}>
                                                  <input
                                                    type="checkbox"
                                                    value={opt.id}
                                                    checked={
                                                      readAnswers[
                                                        q.id
                                                      ]?.includes(opt.id) ||
                                                      false
                                                    }
                                                    onChange={() =>
                                                      handleReadAnswerChange(
                                                        q.id,
                                                        opt.id,
                                                        true,
                                                      )
                                                    }
                                                  />
                                                  <label className="mx-2">
                                                    {getFormQuizOptionText(
                                                      opt,
                                                      selectedLanguage,
                                                    )}
                                                  </label>
                                                </div>
                                              ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* <------------------------ Yes, training Options --------------------------> */}

                        {showDiv && (
                          <>
                            <div className="mt-3">
                              <h6>
                                <b>Number of replies with answer Yes</b>
                                <span className="danger">*</span>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster or instructor tells you the number of
                                replies with yes.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={numberYesOptions}
                                onChange={handleNumberYesOptionsChange}
                                value={formValues.selectednumberYesOptions}
                                autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>

                            <div className="mt-3">
                              <h6>
                                <b>Number of replies with answer No</b>
                                <span className="danger">*</span>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster or instructor tells you the number of
                                replies with no.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={quantityOptions}
                                onChange={handleQuantityOptionsChange}
                                value={formValues.selectedquantityOptions}
                                // autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>

                            <div className="mt-3">
                              <h6>
                                <b>Number of replies with answer NA</b>
                                <span className="danger">*</span>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster or instructor tells you the number of
                                NAs. NA means Not Applicable or Not Relevant.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={numberNAOptions}
                                onChange={handleNumberNAOptionsChange}
                                value={formValues.selectednumberNAOptions}
                                // autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>
                          </>
                        )}

                        {/* <------------------------ Yes, training Options --------------------------> */}

                        <div className="mt-4">
                          <h6>
                            <b>
                              If possible, write a short note or comment on the
                              critical incident that you just have acted on.
                            </b>{" "}
                          </h6>
                        </div>
                        <div className="mt-4">
                          <h6>
                            You can also write a comment about the training or
                            the ACTION-CARD here.
                          </h6>
                        </div>
                        <div className="mt-4 textarea">
                          <textarea
                            name="input_11"
                            id="input_11"
                            className="textarea medium"
                            aria-describedby="gfield_description_61_11"
                            maxLength="4000"
                            aria-invalid="false"
                            rows="10"
                            cols="50"
                            style={{ fontSize: "16px" }}
                            value={formValues.inputValue}
                            onChange={handleTextareaChange}
                          ></textarea>
                        </div>
                        <p
                          style={{
                            fontSize: "14px",
                            // marginTop: -8,
                            // marginLeft: 8,
                            color: "red",
                          }}
                        >
                          {emojiError}
                        </p>
                        <small className="mt-4">{characterCountLabel}</small>

                        <div className="mt-4">
                          <h6>
                            <b>
                              If possible, upload a picture of the area where
                              the incident took place or other necessary
                              documentation.
                            </b>{" "}
                            Max. file size: 10 MB, Max. files: 10.
                          </h6>
                        </div>

                        <div className="form_drop_area">
                          <span className="form_text">Drop file here or</span>
                          <button className="form_button" type="button">
                            <input
                              type="file"
                              accept=".jpg, .jpeg, .png"
                              onChange={handleFileChange}
                              id="upload1"
                              hidden
                            />
                            <label
                              className="label-color-white"
                              htmlFor="upload1"
                            >
                              CHOOSE FILES
                            </label>
                          </button>
                        </div>
                        <div>
                          {errorMsg ? (
                            <p className="form_text">{errorMsg}</p>
                          ) : (
                            <div>
                              <span className="form_text">
                                {formValues.selectedFile.name}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-4">
                          When you have filled in this form correctly, you must
                          press the "SEND" button and wait until "THANK YOU"
                          appears on the screen.
                        </div>
                        <div
                          className="mt-4"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            type="submit"
                            className="form_button"
                            style={{
                              padding: "8px 40px 8px 40px",
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner"
                                  style={{ marginRight: "8px" }}
                                ></span>{" "}
                                {/* Optionally, add a spinner icon */}
                                Loading...
                              </>
                            ) : (
                              "SEND"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div
                className="tab-pane fade show active"
                id="pills-profile"
                role="tabpanel"
                aria-labelledby="pills-profile-tab"
              >
                <form onSubmit={handleSubmit}>
                  <div className="container mt-3">
                    <div className="row">
                      <div className="col-12">
                        <h4>
                          <b>Du har scannet Action-cards®: </b>
                        </h4>
                        <h5 className="mt-3">
                          {selectedLanguage !== "danish"
                            ? actioncardname.eng
                            : actioncardname.danish}
                        </h5>
                        <h6 className="mt-4">
                          Tryk SEND for at fuldende registreringen.{" "}
                        </h6>
                        <h6 className="mt-4">
                          <b>Skarp hændelse</b>
                        </h6>
                        <div className="mt-3">
                          <Select
                            className="basic-single"
                            classNamePrefix="select"
                            options={realIncidentOptions.map((option) => ({
                              value: option.value,
                              label:
                                languageLabels[selectedLanguage][option.label],
                            }))}
                            onChange={handleChange}
                            value={formValues.selected}
                            placeholder={
                              languageLabels[selectedLanguage]["--Select--"]
                            }
                            autoFocus
                            styles={{
                              singleValue: (provided) => ({
                                ...provided,
                                fontSize: "18px",
                              }),
                              option: (provided) => ({
                                ...provided,
                                fontSize: "18px",
                              }),
                            }}
                          />
                        </div>

                        <>
                          <div className="mt-3">
                            <h6>
                              <b>Angiv dit medarbejder navn eller ID</b>
                            </h6>
                            <div className="w-100">
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={trainingOptions.map((option) => ({
                                  value: option.value,
                                  label: option.label,
                                  user_id: option.user_id,
                                }))}
                                onChange={handleTrainingOptionChange}
                                value={formValues.selectedtrainingOption}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>
                          </div>
                          {formValues.selectedtrainingOption?.value ===
                            "Other" && (
                            <div className="mt-3">
                              <label forHtml="name">
                                <h6 className="radiolabel">
                                  <b>Skriv dit e-mail-id her</b>
                                  <span className="danger">*</span>
                                </h6>
                              </label>
                              <div>
                                <input
                                  type="email"
                                  className="form-control"
                                  id="name"
                                  maxLength="45"
                                  value={formValues.name}
                                  style={{ width: "50%", color: "black" }}
                                  onChange={onNameChangeHandler}
                                />
                              </div>
                              <small className="mt-4">
                                {characterCountLabelofName}
                              </small>
                            </div>
                          )}
                        </>

                        {formValues.selected.value === "yes" && (
                          <div>
                            {isYellowRedRequiredOrg(org) && (
                              <div className={"mt-3"}>
                                <h6>
                                  <b>Vælg den rigtige hændelsestype</b>
                                </h6>

                                <div
                                  className={"mt-3 w-100"}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "20px",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <div className="w-100">
                                    <Select
                                      required={true}
                                      className="basic-single"
                                      classNamePrefix="select"
                                      options={yellowRedOption.map(
                                        (option) => ({
                                          value: option.value,
                                          label:
                                            languageLabels[selectedLanguage]?.[
                                              option.label
                                            ] || option.label,
                                        }),
                                      )}
                                      onChange={(selected) => {
                                        handleRealIncidentRedYellow(selected);
                                        if (
                                          selected?.value !== "yellow" ||
                                          org !== "DNK" ||
                                          !["21", "60", "76"].includes(
                                            acNo?.toString(),
                                          )
                                        ) {
                                          setFormValues((prev) => ({
                                            ...prev,
                                            yellowRedNumber: {
                                              value: 1,
                                              label: "1",
                                            },
                                          }));
                                        }
                                      }}
                                      value={
                                        yellowRedOption
                                          .map((option) => ({
                                            value: option.value,
                                            label:
                                              languageLabels[
                                                selectedLanguage
                                              ]?.[option.label] || option.label,
                                          }))
                                          .find(
                                            (opt) =>
                                              opt.value ===
                                              formValues.yellowRed?.value,
                                          ) || null
                                      }
                                      placeholder={
                                        languageLabels[selectedLanguage]?.[
                                          "--Select--"
                                        ] || "--Select--"
                                      }
                                      styles={{
                                        singleValue: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                        option: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                        placeholder: (provided) => ({
                                          ...provided,
                                          fontSize: "18px",
                                        }),
                                      }}
                                    />
                                  </div>

                                  {formValues.yellowRed?.value === "yellow" &&
                                    org === "DNK" &&
                                    ["21", "60", "76"].includes(
                                      acNo?.toString(),
                                    ) && (
                                      <div className="w-100">
                                        <h6>
                                          <b>Vælg antal</b>
                                        </h6>

                                        <div className="w-100">
                                          <Select
                                            required
                                            classNamePrefix="select"
                                            placeholder="1"
                                            options={Array.from(
                                              { length: 30 },
                                              (_, i) => ({
                                                value: i + 1,
                                                label: `${i + 1}`,
                                              }),
                                            )}
                                            onChange={(selected) => {
                                              setFormValues((prev) => ({
                                                ...prev,
                                                yellowRedNumber: selected,
                                              }));
                                            }}
                                            value={
                                              formValues.yellowRedNumber || null
                                            }
                                            styles={{
                                              singleValue: (provided) => ({
                                                ...provided,
                                                fontSize: "18px",
                                              }),
                                              option: (provided) => ({
                                                ...provided,
                                                fontSize: "18px",
                                              }),
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <h6>
                                <b>
                                  Til den tilskadekommende: Vil det være i orden
                                  med dig, at vi registrerer dit fulde navn og
                                  kontaktoplysninger?
                                </b>
                              </h6>
                              <span className="mt-5">
                                <h6>
                                  Oplysningerne bruges kun til at du selv, ved
                                  forespørgsel, kan dokumentere denne hændelse.
                                  Dine personlige oplysninger (dvs. navn og
                                  kontaktoplysninger) slettes automatisk fra
                                  systemet efter 6 måneder.
                                </h6>
                              </span>
                              <div className="w-full mt-3">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  options={injuredParty.map((option) => ({
                                    value: option.value,
                                    label:
                                      languageLabels[selectedLanguage]?.[
                                        option.label
                                      ] || option.label,
                                  }))}
                                  onChange={handleInjuredPartyOptionChange}
                                  value={
                                    injuredParty
                                      .map((option) => ({
                                        value: option.value,
                                        label:
                                          languageLabels[selectedLanguage]?.[
                                            option.label
                                          ] || option.label,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formValues.selectedInjuredPartyOption
                                            ?.value,
                                      ) || null
                                  }
                                  placeholder={
                                    languageLabels[selectedLanguage]?.[
                                      "--Select--"
                                    ] || "--Select--"
                                  }
                                  styles={{
                                    singleValue: (provided) => ({
                                      ...provided,
                                      fontSize: "18px",
                                    }),
                                    option: (provided) => ({
                                      ...provided,
                                      fontSize: "18px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>

                            {formValues.selectedInjuredPartyOption.value ===
                              "yes" && (
                              <div>
                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Fornavn og efternavn </b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyName}
                                      onChange={onInjuredPartyNameChangeHandler}
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Telefonnummer</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyPhoneNo}
                                      onChange={
                                        onInjuredPartyPhoneNoChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Email</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.injuredPartyEmail}
                                      onChange={
                                        onInjuredPartyEmailChangeHandler
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <h6>
                                <b>
                                  Til evt vidne: Vil det være i orden med dig,
                                  at vi registrerer dit fulde navn og
                                  kontaktoplysninger?
                                </b>
                              </h6>
                              <span className="mt-5">
                                <h6>
                                  Oplysningerne gemmes kun i forbindelse med
                                  evt. dokummentation af ulykken. Dine
                                  personlige oplysninger (dvs. navn og
                                  kontaktoplysninger) slettes automatisk fra
                                  systemet efter 6 måneder.
                                </h6>
                              </span>
                              <div className="w-full mt-3">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  options={potentialWitnesses.map((option) => ({
                                    value: option.value,
                                    label:
                                      languageLabels[selectedLanguage]?.[
                                        option.label
                                      ] || option.label,
                                  }))}
                                  onChange={handlePotentialWitnessesChange}
                                  value={
                                    potentialWitnesses
                                      .map((option) => ({
                                        value: option.value,
                                        label:
                                          languageLabels[selectedLanguage]?.[
                                            option.label
                                          ] || option.label,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formValues
                                            .selectedPotentialWitnessesOptions
                                            ?.value,
                                      ) || null
                                  }
                                  placeholder={
                                    languageLabels[selectedLanguage]?.[
                                      "--Select--"
                                    ] || "--Select--"
                                  }
                                  styles={{
                                    singleValue: (provided) => ({
                                      ...provided,
                                      fontSize: "18px",
                                    }),
                                    option: (provided) => ({
                                      ...provided,
                                      fontSize: "18px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>

                            {formValues.selectedPotentialWitnessesOptions
                              .value === "yes" && (
                              <div>
                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Fornavn og efternavn</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.potentialWitnessesName}
                                      onChange={
                                        onPotentialWitnessesNameChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Telefonnummer</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={
                                        formValues.potentialWitnessesPhoneNo
                                      }
                                      onChange={
                                        onPotentialWitnessesPhoneNoChangeHandler
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <label forHtml="name">
                                    <h6 className="radiolabel">
                                      <b>Email</b>
                                      <span className="danger">*</span>
                                    </h6>
                                  </label>
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="name"
                                      maxLength="45"
                                      style={{ color: "black" }}
                                      value={formValues.potentialWitnessesEmail}
                                      onChange={
                                        onPotentialWitnessesEmailChangeHandler
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {formValues.selected.value === "no" && (
                          <div>
                            <div className="mt-4">
                              <h6>
                                <b>
                                  ER QR kode skannet for at registere træning
                                  eller læsning af Action-cardet?
                                </b>{" "}
                              </h6>
                            </div>
                            <div className="mt-4">
                              <input
                                id="contactChoice1"
                                name="contact"
                                type="radio"
                                value="training"
                                checked={selectedOption === "training"}
                                onChange={handleRadioChange}
                              />
                              <label
                                for="contactChoice1"
                                className="radiolabel mx-2"
                                style={{ fontSize: "18px" }}
                              >
                                Ja, træning
                              </label>
                              <br />
                              <input
                                type="radio"
                                id="contactChoice2"
                                name="contact"
                                value="read"
                                checked={selectedOption === "read"}
                                onChange={handleRadioChange}
                              />
                              <label
                                for="contactChoice2"
                                className="radiolabel mx-2"
                                style={{ fontSize: "18px" }}
                              >
                                Ja, læst
                              </label>
                              {canShowReportingCategoryOptions && (
                                <>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoiceNearMissDk"
                                    name="contact"
                                    value="near_miss"
                                    checked={selectedOption === "near_miss"}
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoiceNearMissDk"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Near miss
                                  </label>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoicePreventionDk"
                                    name="contact"
                                    value="prevention"
                                    checked={selectedOption === "prevention"}
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoicePreventionDk"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Prevention
                                  </label>
                                  <br />
                                  <input
                                    type="radio"
                                    id="contactChoiceDoubtDk"
                                    name="contact"
                                    value="doubt_about_reporting"
                                    checked={
                                      selectedOption === "doubt_about_reporting"
                                    }
                                    onChange={handleRadioChange}
                                  />
                                  <label
                                    htmlFor="contactChoiceDoubtDk"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Doubt about reporting
                                  </label>
                                </>
                              )}
                            </div>
                            {selectedOption === "read" && (
                              <div className="mt-3">
                                <p
                                  className="radiolabel"
                                  style={{ fontSize: "18px" }}
                                >
                                  Du har læst et Action Card. Vil du øge din
                                  score ved at besvare tre spørgsmål?
                                </p>
                                <div>
                                  <input
                                    id="readChoiceYesDk"
                                    name="readChoiceDk"
                                    type="radio"
                                    value="yes"
                                    checked={readQuizChoice === "yes"}
                                    onChange={() => handleReadChoice("yes")}
                                  />
                                  <label
                                    htmlFor="readChoiceYesDk"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Ja
                                  </label>
                                  <br />
                                  <input
                                    id="readChoiceNoDk"
                                    name="readChoiceDk"
                                    type="radio"
                                    value="no"
                                    checked={readQuizChoice === "no"}
                                    onChange={() => handleReadChoice("no")}
                                  />
                                  <label
                                    htmlFor="readChoiceNoDk"
                                    className="radiolabel mx-2"
                                    style={{ fontSize: "18px" }}
                                  >
                                    Nej
                                  </label>
                                </div>

                                {readQuizError && (
                                  <p
                                    className="text-danger"
                                    style={{ fontSize: "16px" }}
                                  >
                                    {readQuizError}
                                  </p>
                                )}

                                {readQuiz && !readQuizError && (
                                  <div className="mt-3">
                                    {readQuiz.questions?.map((q, idx) => (
                                      <div key={q.id} className="mb-3">
                                        <p className="radiolabel">
                                          <b>
                                            Q{idx + 1}:{" "}
                                            {getFormQuizQuestionText(
                                              q,
                                              selectedLanguage,
                                            )}
                                          </b>
                                        </p>
                                        <div style={{ paddingLeft: "1rem" }}>
                                          {q.correct_answers === 1
                                            ? q.options?.map((opt) => (
                                                <div key={opt.id}>
                                                  <input
                                                    type="radio"
                                                    name={`read-q-${q.id}`}
                                                    value={opt.id}
                                                    checked={
                                                      readAnswers[q.id]?.[0] ===
                                                      opt.id
                                                    }
                                                    onChange={() =>
                                                      handleReadAnswerChange(
                                                        q.id,
                                                        opt.id,
                                                        false,
                                                      )
                                                    }
                                                  />
                                                  <label className="mx-2">
                                                    {getFormQuizOptionText(
                                                      opt,
                                                      selectedLanguage,
                                                    )}
                                                  </label>
                                                </div>
                                              ))
                                            : q.options?.map((opt) => (
                                                <div key={opt.id}>
                                                  <input
                                                    type="checkbox"
                                                    value={opt.id}
                                                    checked={
                                                      readAnswers[
                                                        q.id
                                                      ]?.includes(opt.id) ||
                                                      false
                                                    }
                                                    onChange={() =>
                                                      handleReadAnswerChange(
                                                        q.id,
                                                        opt.id,
                                                        true,
                                                      )
                                                    }
                                                  />
                                                  <label className="mx-2">
                                                    {getFormQuizOptionText(
                                                      opt,
                                                      selectedLanguage,
                                                    )}
                                                  </label>
                                                </div>
                                              ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* <------------------------ Yes, training Options --------------------------> */}

                        {showDiv && (
                          <>
                            <div className="mt-3">
                              <h6>
                                <b>Antal Ja *</b>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster eller instruktør fortæller dig
                                antallet af Ja’er.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={numberYesOptions}
                                onChange={handleNumberYesOptionsChange}
                                value={formValues.selectednumberYesOptions}
                                autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>

                            <div className="mt-3">
                              <h6>
                                <b>Antal Nej</b>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster eller instruktør fortæller dig
                                antallet af Nej’er.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={quantityOptions}
                                onChange={handleQuantityOptionsChange}
                                value={formValues.selectedquantityOptions}
                                // autoFocus={true}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                              />
                            </div>

                            <div className="mt-3">
                              <h6>
                                <b>Antal NA</b>
                              </h6>
                              <h6 className="mt-3">
                                Gamemaster eller instruktør fortæller dig
                                antallet af NA’er. NA betyder Not Applicable
                                eller Ikke Relevant.
                              </h6>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                options={numberNAOptions}
                                onChange={handleNumberNAOptionsChange}
                                value={formValues.selectednumberNAOptions}
                                styles={{
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  option: (provided) => ({
                                    ...provided,
                                    fontSize: "18px", // Adjust the font size as needed
                                  }),
                                  // Add more styles as needed
                                }}
                                // autoFocus={true}
                              />
                            </div>
                          </>
                        )}

                        {/* <------------------------ Yes, training Options --------------------------> */}

                        <div className="mt-4">
                          <h6>
                            <b>
                              Hvis muligt: Skriv en kort note eller kommentar
                              til den kritiske hændelse, som du netop har været
                              med til at handle på.
                            </b>{" "}
                          </h6>
                        </div>
                        <div className="mt-4">
                          <h6>
                            Du kan også skrive en bemærkning til træningen eller
                            Action-cardet her.
                          </h6>
                        </div>
                        <div className="mt-4 textarea">
                          <textarea
                            name="input_11"
                            id="input_11"
                            className="textarea medium"
                            aria-describedby="gfield_description_61_11"
                            maxLength="4000"
                            aria-invalid="false"
                            rows="10"
                            cols="50"
                            value={formValues.inputValue}
                            onChange={handleTextareaChange}
                            style={{ fontSize: "16px" }}
                          ></textarea>
                        </div>
                        <p
                          style={{
                            fontSize: "14px",
                            // marginTop: -8,
                            // marginLeft: 8,
                            color: "red",
                          }}
                        >
                          {emojiError}
                        </p>
                        <small className="mt-4">{characterCountLabel}</small>

                        <div className="mt-4">
                          <h6>
                            <b>Upload billede dokumentation</b> Max. file size:
                            10 MB, Max. files: 10.
                          </h6>
                        </div>

                        <div className="form_drop_area">
                          <span className="form_text">Slip fil her eller</span>
                          <button className="form_button" type="button">
                            <input
                              type="file"
                              accept=".jpg, .jpeg, .png"
                              onChange={handleFileChange}
                              id="upload1"
                              hidden
                            />
                            <label
                              className="label-color-white"
                              htmlFor="upload1"
                            >
                              Vælg filer
                            </label>
                          </button>
                        </div>
                        <div>
                          {errorMsg ? (
                            <p className="form_text">{errorMsg}</p>
                          ) : (
                            <div>
                              <span className="form_text">
                                {formValues.selectedFile.name}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-4">
                          <h6>
                            Når du har udfyldt denne form korrekt, så skal du
                            trykke på knappen ”SEND” og vente til der står
                            ”TAK”, på skærmen.
                          </h6>
                        </div>
                        <div
                          className="mt-4"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            type="submit"
                            className="form_button"
                            style={{
                              padding: "8px 40px 8px 40px",
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner"
                                  style={{ marginRight: "8px" }}
                                ></span>
                                Loading...
                              </>
                            ) : (
                              "SEND"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ActionCardsForm;
